import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import { BestOf2025PhaseTwoResourcesContext } from 'app/components/BestOf2025/BestOf2025PhaseTwo/BestOf2025PhaseTwoResources/BestOf2025PhaseTwoResourcesContext';
import { BestOf2025Phase, BestOf2025Phases } from 'app/components/BestOf2025/interfaces';
import { AddIcon, RemoveIcon, Spinner, Switch, SwitcherIcon } from 'app/components/shared/icons';
import CheckIcon from 'app/components/shared/icons/check-icon';
import { AnimatePresence } from 'framer-motion';
import { deleteBestOfYearsVote, putBestOfYearsVote } from 'services/best-of-years';
import {
  bestOfMyVotesByCategorySelector,
  bestOfMyVotesSelector,
  bestOfPhaseSelector,
  bestOfVotingSelector,
  pushMyVote,
  removeMyVote,
  setMyVotesByCategory,
} from 'store/reducers/best-of/best-of-reducer';
import { ThemeProvider } from 'styled-components';

import ResourceDetailBestOfAward from '../ResourceDetailBestOfAward';
import { ResourceDetailContext } from '../ResourceDetailContext';
import {
  ResourceDetailVoteButton,
  ResourceDetailVoteButtonCheckContainer,
  ResourceDetailVoteButtonContainer,
  ResourceDetailVoteButtonLabel,
  ResourceDetailVoteContainer,
  ResourceDetailVoteIcon,
  ResourceDetailVoteIconContainer,
  ResourceDetailVoteText,
  ResourceDetailVoteWrapper,
} from './styled';

const RESOURCE_DETAIL_VOTE_STRATEGY = {
  VOTED: 'VOTED',
  REMOVE_VOTE: 'REMOVE_VOTE',
  SWITCH_VOTE: 'SWITCH_VOTE',
  VOTE: 'VOTE',
};

export type RESOURCE_DETAIL_VOTE_STRATEGY =
  (typeof RESOURCE_DETAIL_VOTE_STRATEGY)[keyof typeof RESOURCE_DETAIL_VOTE_STRATEGY];

export default function ResourceDetailVote() {
  const dispatch = useDispatch();
  const { data } = useContext(ResourceDetailContext);
  const phase: BestOf2025Phase = useSelector(bestOfPhaseSelector);
  const voting = useSelector(bestOfVotingSelector);
  const myVotes = useSelector(bestOfMyVotesSelector);
  const votesByCategory = useSelector(bestOfMyVotesByCategorySelector);
  const [hasBeenVoted, setHasBeenVoted] = useState<boolean>(false);

  // Phase Two context (may be null if not in Phase Two)
  const phaseTwoContext = useContext(BestOf2025PhaseTwoResourcesContext);

  const isPhaseTwo = useMemo<boolean>(() => {
    return [
      BestOf2025Phases.PHASE_TWO_VOTING as BestOf2025Phase,
      BestOf2025Phases.PHASE_TWO_VOTING_RESOURCES as BestOf2025Phase,
    ].includes(phase);
  }, [phase]);

  const isVoted = useMemo<boolean>(() => {
    if (isPhaseTwo && phaseTwoContext) {
      return phaseTwoContext.selectedResourceIds.includes(data?.uuid);
    }
    return myVotes.includes(data?.uuid);
  }, [myVotes, data?.uuid, isPhaseTwo, phaseTwoContext, phaseTwoContext.selectedResourceIds]);

  const categoryAlreadyHasVote = useMemo<boolean>(() => {
    if (isPhaseTwo && phaseTwoContext) {
      // In Phase Two, check if we have at least 1 vote
      return phaseTwoContext.selectedResourceIds.length > 0;
    }
    return (
      votesByCategory[data?.resource_submission_category?.name] &&
      votesByCategory[data?.resource_submission_category?.name]?.length > 0
    );
  }, [votesByCategory, data?.resource_submission_category?.name, isPhaseTwo, phaseTwoContext]);

  const phaseTwoVotesCount = useMemo<number>(() => {
    if (!isPhaseTwo || !phaseTwoContext) return 0;
    return phaseTwoContext.selectedResourceIds.length;
  }, [isPhaseTwo, phaseTwoContext]);

  const handleBestOfYearsVote = useCallback(async () => {
    if (isVoted) {
      await deleteBestOfYearsVote(2025, data?.uuid);
    } else {
      await putBestOfYearsVote(2025, data?.uuid, categoryAlreadyHasVote);
    }
  }, [data?.uuid, categoryAlreadyHasVote]);

  const strategy = useMemo<RESOURCE_DETAIL_VOTE_STRATEGY>(() => {
    if (isPhaseTwo && phaseTwoContext) {
      // Phase Two logic
      if (isVoted) return RESOURCE_DETAIL_VOTE_STRATEGY.REMOVE_VOTE;
      if (phaseTwoVotesCount >= 2) return RESOURCE_DETAIL_VOTE_STRATEGY.SWITCH_VOTE;
      return RESOURCE_DETAIL_VOTE_STRATEGY.VOTE;
    }

    // Phase One logic
    if (hasBeenVoted) return RESOURCE_DETAIL_VOTE_STRATEGY.VOTED;
    if (isVoted) return RESOURCE_DETAIL_VOTE_STRATEGY.REMOVE_VOTE;
    if (categoryAlreadyHasVote) return RESOURCE_DETAIL_VOTE_STRATEGY.SWITCH_VOTE;
    return RESOURCE_DETAIL_VOTE_STRATEGY.VOTE;
  }, [
    hasBeenVoted,
    isVoted,
    categoryAlreadyHasVote,
    isPhaseTwo,
    phaseTwoContext,
    phaseTwoVotesCount,
  ]);

  const label = useMemo<string>(() => {
    switch (strategy) {
      case RESOURCE_DETAIL_VOTE_STRATEGY.VOTED:
        return 'VOTED';
      case RESOURCE_DETAIL_VOTE_STRATEGY.REMOVE_VOTE:
        return 'REMOVE VOTE';
      case RESOURCE_DETAIL_VOTE_STRATEGY.SWITCH_VOTE:
        if (isPhaseTwo && phaseTwoContext) {
          return 'VOTE';
        }
        return 'SWITCH VOTE';
      default:
        return 'VOTE';
    }
  }, [strategy, isPhaseTwo, phaseTwoContext, phaseTwoVotesCount, isVoted]);

  const description = useMemo<string>(() => {
    switch (strategy) {
      case RESOURCE_DETAIL_VOTE_STRATEGY.REMOVE_VOTE:
        return 'Your pick for %category%';
      case RESOURCE_DETAIL_VOTE_STRATEGY.SWITCH_VOTE:
        if (isPhaseTwo && phaseTwoContext) {
          return 'You have already voted in this category. Remove an existing vote in this category to switch your vote.';
        }
        return 'You have already voted in this category. Would you like to vote for this submission instead?';
      default:
        return 'Nominated for %category%';
    }
  }, [strategy, isPhaseTwo, phaseTwoContext, phaseTwoVotesCount, isVoted]);

  const icon = useMemo<React.ReactNode>(() => {
    switch (strategy) {
      case RESOURCE_DETAIL_VOTE_STRATEGY.VOTED:
        return (
          <ResourceDetailVoteButtonCheckContainer>
            <CheckIcon width={15} height={15} fill="#FFFFFF" withoutBorder />
          </ResourceDetailVoteButtonCheckContainer>
        );
      case RESOURCE_DETAIL_VOTE_STRATEGY.REMOVE_VOTE:
        return <RemoveIcon width={24} height={24} fill="var(--color-primary)" fillOpacity="1" />;
      case RESOURCE_DETAIL_VOTE_STRATEGY.SWITCH_VOTE:
        return <Switch width={18} height={18} fill="var(--color-primary)" />;
      default:
        return <AddIcon width={24} height={24} fill="var(--color-primary)" />;
    }
  }, [strategy]);

  const { mutate, isLoading } = useMutation(handleBestOfYearsVote, {
    onSuccess: () => {
      if (isVoted) {
        dispatch(removeMyVote(data?.uuid));
      } else {
        setHasBeenVoted(true);
        if (categoryAlreadyHasVote) {
          const alreadyVotedResource = votesByCategory[data?.resource_submission_category?.name][0];
          dispatch(removeMyVote(alreadyVotedResource));
          dispatch(pushMyVote(data?.uuid));
        } else {
          dispatch(pushMyVote(data?.uuid));
        }
      }
    },
  });

  const notInVotingPhase = useMemo<boolean>(() => {
    return (
      phase === null ||
      ![
        BestOf2025Phases.PHASE_ONE_VOTING as BestOf2025Phase,
        BestOf2025Phases.PHASE_ONE_VOTING_RESOURCES as BestOf2025Phase,
        BestOf2025Phases.PHASE_ONE_VOTING_ENDED as BestOf2025Phase,
        BestOf2025Phases.PHASE_ONE_VOTING_TIEBREAK as BestOf2025Phase,
        BestOf2025Phases.PHASE_TWO_VOTING as BestOf2025Phase,
        BestOf2025Phases.PHASE_TWO_VOTING_RESOURCES as BestOf2025Phase,
        BestOf2025Phases.PHASE_TWO_VOTING_ENDED as BestOf2025Phase,
      ].includes(phase)
    );
  }, [phase]);

  useEffect(() => {
    let timeoutId = null;
    if (hasBeenVoted) {
      timeoutId = setTimeout(() => {
        setHasBeenVoted(false);
      }, 1000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [hasBeenVoted]);

  const handleVoteButtonClick = useCallback(() => {
    if (isPhaseTwo && phaseTwoContext) {
      phaseTwoContext.handleToggleVote(data?.uuid);
    } else {
      mutate();
    }
  }, [mutate, isPhaseTwo, phaseTwoContext, data?.uuid]);

  if (!voting || notInVotingPhase) {
    if (['winner', 'honoree'].includes(data?.best_of_year?.status)) {
      return <ResourceDetailBestOfAward />;
    } else {
      return null;
    }
  }

  return (
    <ThemeProvider
      theme={{
        isVoted: isVoted && !hasBeenVoted && !isLoading,
      }}
    >
      <ResourceDetailVoteWrapper>
        <ResourceDetailVoteContainer>
          <ResourceDetailVoteIconContainer>
            <ResourceDetailVoteIcon src={data?.resource_submission_category?.icon} />
          </ResourceDetailVoteIconContainer>
          <ResourceDetailVoteText key={description}>
            {description.replace('%category%', data?.resource_submission_category?.name)}
          </ResourceDetailVoteText>
        </ResourceDetailVoteContainer>
        <AnimatePresence mode="popLayout" initial={false}>
          <ResourceDetailVoteButton
            onClick={handleVoteButtonClick}
            disabled={strategy === RESOURCE_DETAIL_VOTE_STRATEGY.SWITCH_VOTE && isPhaseTwo}
          >
            <ResourceDetailVoteButtonContainer key={isLoading ? 'loading' : label}>
              {!isLoading ? icon : null}
              {!isLoading ? (
                <ResourceDetailVoteButtonLabel>{label}</ResourceDetailVoteButtonLabel>
              ) : null}
              {isLoading ? <Spinner width={24} height={24} fill="var(--color-primary)" /> : null}
            </ResourceDetailVoteButtonContainer>
          </ResourceDetailVoteButton>
        </AnimatePresence>
      </ResourceDetailVoteWrapper>
    </ThemeProvider>
  );
}
